// Firebase Auth service – real implementations using Firebase Auth.

import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  updateProfile,
  checkActionCode,
  applyActionCode,
  confirmPasswordReset,
  onAuthStateChanged,
  type User,
  type ActionCodeInfo,
} from "firebase/auth";

import { firebaseApp } from "./firebase.config";

const auth = getAuth(firebaseApp);

/**
 * Centralised action handler settings
 * Used for BOTH verification and password reset
 */
const actionCodeSettings = {
  url: "https://app.coretrak.fit/auth/action",
  handleCodeInApp: true,
};

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  emailVerified?: boolean;
}

function mapFirebaseUser(user: User): AuthUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    emailVerified: user.emailVerified,
  };
}

/**
 * SIGN IN
 */
export async function signInWithEmail(
  email: string,
  password: string,
  keepLoggedIn: boolean = true,
): Promise<AuthUser> {
  await setPersistence(
    auth,
    keepLoggedIn ? browserLocalPersistence : browserSessionPersistence,
  );

  const userCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password,
  );

  return mapFirebaseUser(userCredential.user);
}

/**
 * CREATE ACCOUNT
 */
export async function createUserWithEmail(
  email: string,
  password: string,
): Promise<AuthUser> {
  await setPersistence(auth, browserLocalPersistence);

  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password,
  );

  console.log("[Auth] Sending verification email...");

  await sendEmailVerification(
    userCredential.user,
    actionCodeSettings,
  );

  console.log("[Auth] Verification email sent:", email);

  return mapFirebaseUser(userCredential.user);
}

/**
 * PASSWORD RESET — FIXED
 */
export async function sendPasswordReset(
  email: string,
): Promise<void> {
  try {
    console.log("[Auth] Sending password reset email:", email);

    await sendPasswordResetEmail(
      auth,
      email,
      actionCodeSettings,
    );

    console.log(
      "[Auth] Password reset email successfully sent:",
      email,
    );
  } catch (error) {
    console.error("[Auth] Password reset failed:", error);

    const message =
      error instanceof Error
        ? error.message
        : "Failed to send password reset email";

    throw new Error(message);
  }
}

/**
 * SIGN OUT
 */
export async function signOutUser(): Promise<void> {
  await signOut(auth);

  try {
    localStorage.removeItem(
      "coretrak.home.calorieBalanceExpanded",
    );
  } catch {
    // ignore cleanup failure
  }
}

/**
 * WAIT FOR AUTH READY
 */
export function waitForAuthReady(
  expectedUid?: string,
  timeoutMs = 10000,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unsubscribe();
      reject(
        new Error(
          "Auth did not settle within timeout.",
        ),
      );
    }, timeoutMs);

    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        if (!user) return;
        if (expectedUid && user.uid !== expectedUid) return;

        try {
          await user.getIdToken(false);
          clearTimeout(timer);
          unsubscribe();
          resolve();
        } catch {
          try {
            await user.getIdToken(true);
            clearTimeout(timer);
            unsubscribe();
            resolve();
          } catch (err) {
            clearTimeout(timer);
            unsubscribe();
            reject(err);
          }
        }
      },
    );
  });
}

/**
 * CURRENT USER
 */
export function getCurrentUser(): AuthUser | null {
  const user = auth.currentUser;
  return user ? mapFirebaseUser(user) : null;
}

/**
 * UPDATE DISPLAY NAME
 */
export async function updateDisplayName(
  displayName: string,
): Promise<void> {
  const user = auth.currentUser;

  if (!user) {
    throw new Error("No user signed in.");
  }

  await updateProfile(user, { displayName });
}

/**
 * ACTION HANDLER — VERIFY / RESET FLOW SUPPORT
 */

export async function verifyActionCode(
  oobCode: string,
): Promise<ActionCodeInfo> {
  return checkActionCode(auth, oobCode);
}

export async function applyVerificationCode(
  oobCode: string,
): Promise<void> {
  await applyActionCode(auth, oobCode);
}

export async function resetPasswordWithCode(
  oobCode: string,
  newPassword: string,
): Promise<void> {
  await confirmPasswordReset(
    auth,
    oobCode,
    newPassword,
  );
}

// ─── Aliases expected by auth screens ─────────────────────────────────────────

/** AuthActionScreen expects validateActionCode */
export const validateActionCode = verifyActionCode;

/** AuthActionScreen expects applyEmailVerificationCode */
export const applyEmailVerificationCode = applyVerificationCode;

/** AuthActionScreen expects confirmPasswordResetWithCode */
export const confirmPasswordResetWithCode = resetPasswordWithCode;

/**
 * RESEND VERIFICATION EMAIL
 * Sends a new verification email to the currently signed-in user.
 * Used by EmailVerificationScreen and EmailVerificationBanner.
 */
export async function resendVerificationEmail(): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('No user signed in.');
  await sendEmailVerification(user, actionCodeSettings);
}

/**
 * RESEND VERIFICATION EMAIL WITH CREDENTIALS
 * Signs in with email/password, sends verification email, then signs out.
 * Used by CheckEmailScreen where the user may not be signed in.
 */
export async function resendVerificationEmailWithCredentials(
  email: string,
  password: string,
): Promise<void> {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  await sendEmailVerification(userCredential.user, actionCodeSettings);
  await signOut(auth);
}

/**
 * RELOAD USER
 * Reloads the current user's auth state from Firebase.
 * Returns the updated AuthUser or null.
 */
export async function reloadUser(): Promise<AuthUser | null> {
  const user = auth.currentUser;
  if (!user) return null;
  await user.reload();
  const fresh = auth.currentUser;
  return fresh ? mapFirebaseUser(fresh) : null;
}